import { Box, Button, Collapse, Flex, Icon, Text } from "@chakra-ui/react";
import { FiChevronDown, FiPlus } from "react-icons/fi";
import { useLocation } from "react-router-dom";
import { useSub } from "../../subscription/useSub";
import type { AppRole, NavSection, NavSectionAction, SectionActionKind } from "./navConfig";
import { isNavItemActive } from "./navActive";
import { NavBadgeCount, SidebarNavItem } from "./SidebarNavItem";
import { useNavLabel } from "./useNavLabel";
import type { NavBadges } from "./useNavBadges";

interface Props {
  section: NavSection;
  role: AppRole;
  badges: NavBadges;
  isCollapsed: boolean;
  onToggle: (sectionKey: string) => void;
  onNavigate: (path: string, query?: string) => void;
  onAction: (action: NavSectionAction) => void;
  pendingActionKind?: SectionActionKind | null;
}

export const SidebarSection = ({
  section,
  role,
  badges,
  isCollapsed,
  onToggle,
  onNavigate,
  onAction,
  pendingActionKind,
}: Props) => {
  const navLabel = useNavLabel();
  const location = useLocation();
  const sub = useSub();

  const visibleItems = section.items.filter(
    (item) => item.roles.includes(role) && (!item.access || sub[item.access]),
  );
  const action = section.action;
  const showAction = action != null && action.roles.includes(role) && (!action.access || sub[action.access]);

  // Role or tenant config hid everything → no section row at all, not even an empty collapsed one.
  if (visibleItems.length === 0 && !showAction) return null;

  // A section that would fold out to exactly its own name reads as a plain row instead — with the
  // same weight as the foldable headers, so rows on one level don't look like two levels.
  if (visibleItems.length === 1 && visibleItems[0].labelKey === section.titleKey && !showAction) {
    const item = visibleItems[0];
    return (
      <SidebarNavItem
        item={item}
        topLevel
        isActive={isNavItemActive(item, location.pathname, location.search)}
        badge={item.badgeKey ? badges[item.badgeKey] : undefined}
        onClick={() => onNavigate(item.path, item.query)}
      />
    );
  }

  // Only the badges of items this user can see: a collapsed sum must never advertise a count the
  // user cannot open.
  const badgeSum = visibleItems.reduce((sum, item) => sum + (item.badgeKey ? badges[item.badgeKey] ?? 0 : 0), 0);

  return (
    <Box mb="0.5">
      <Flex
        as="button"
        type="button"
        aria-expanded={!isCollapsed}
        onClick={() => onToggle(section.titleKey)}
        align="center"
        gap="3"
        w="full"
        px="3"
        py="2"
        rounded="lg"
        textAlign="left"
        color="smText"
        fontWeight="medium"
        fontSize="sm"
        transition="background-color 0.15s"
        _hover={{ bg: "smBackgroundSecondary" }}
        // Chakra's Collapse hard-codes overflow:hidden inline on the submenu, and the aside clips
        // too, so a ring drawn outside the row would be cut on every side. This one is inside it.
        _focusVisible={{
          boxShadow: "inset 0 0 0 2px var(--chakra-colors-smPrimary)",
          outline: "none",
        }}
      >
        <Icon as={section.icon} boxSize="18px" flexShrink={0} />
        <Text flex="1" isTruncated title={navLabel(section.titleKey)}>
          {navLabel(section.titleKey)}
        </Text>
        {/* The sum stands in for the hidden per-item badges; expanded they carry their own. */}
        {isCollapsed && badgeSum > 0 && <NavBadgeCount value={badgeSum} />}
        <Icon
          as={FiChevronDown}
          boxSize="4"
          color="smMuted"
          flexShrink={0}
          transform={isCollapsed ? "rotate(-90deg)" : undefined}
          transition="transform 0.15s"
        />
      </Flex>

      <Collapse in={!isCollapsed} animateOpacity>
        <Box pb="1">
          {visibleItems.map((item) => (
            <SidebarNavItem
              key={`${item.labelKey}-${item.query ?? ""}`}
              item={item}
              isActive={isNavItemActive(item, location.pathname, location.search)}
              badge={item.badgeKey ? badges[item.badgeKey] : undefined}
              onClick={() => onNavigate(item.path, item.query)}
            />
          ))}

          {showAction && action && (
            <Button
              mt="1"
              mb="1"
              w="full"
              size="sm"
              colorScheme="teal"
              leftIcon={<Icon as={FiPlus} />}
              isLoading={pendingActionKind === action.kind}
              onClick={() => onAction(action)}
            >
              {navLabel(action.labelKey)}
            </Button>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
